"use client"
import React, { useEffect } from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { marked } from "marked"

/**
 * Full-screen reader for one block or example, with its markdown rendered.
 *
 * Rendered through a portal on <body>. `position: fixed` is resolved against
 * the nearest ancestor with a transform or a backdrop-filter — and the
 * dashboard has several — which left the overlay anchored to the content
 * column and spilling under the sidebar rail.
 */
export default function SectionModal({ entry, onClose }) {
  useEffect(() => {
    if (!entry) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [entry, onClose]);

  if (!entry) return null;

  const isExample = entry.type === "example";
  const fallbackTitle = `${isExample ? "Example" : "Section"} ${entry.index + 1}`;

  // The overlay never scrolls — the body inside does. Letting the page grow
  // instead stretched the panel past the screen on a long block.
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-black/95 p-4 backdrop-blur-lg animate-in fade-in duration-200 sm:p-8"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-black/95 shadow-2xl animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-shrink-0 items-start justify-between gap-3 border-b border-white/10 bg-black/60 px-4 py-4 sm:px-8 sm:py-6">
          <div className="min-w-0">
            <span
              className={`inline-block rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider sm:text-xs ${
                isExample
                  ? "border border-orange-500/30 bg-orange-500/20 text-orange-300"
                  : "border border-purple-500/30 bg-purple-500/20 text-purple-300"
              }`}
            >
              {isExample ? "Example" : "Section"}
            </span>
            <h3 className="mt-2 break-words text-lg font-semibold text-white sm:text-2xl">
              {entry.title || entry.name || fallbackTitle}
            </h3>
          </div>

          <button
            onClick={onClose}
            aria-label="Close"
            className="group flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-gray-400 transition-all duration-200 hover:border-white/20 hover:bg-white/10 hover:text-white sm:h-10 sm:w-10"
          >
            <X className="h-5 w-5 transition-transform duration-200 group-hover:rotate-90" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-8 sm:py-6">
          <div
            className="prose prose-invert max-w-none break-words leading-relaxed text-gray-300 prose-sm sm:prose-lg
              prose-headings:font-semibold prose-headings:text-white
              prose-h1:mb-4 prose-h1:text-purple-300
              prose-h2:mb-3 prose-h2:text-purple-400
              prose-h3:mb-2 prose-h3:text-purple-500
              prose-p:mb-4 prose-p:text-gray-300
              prose-a:text-purple-400 prose-a:no-underline hover:prose-a:text-purple-300
              prose-strong:font-semibold prose-strong:text-white
              prose-code:rounded prose-code:bg-purple-900/20 prose-code:px-1.5 prose-code:py-0.5 prose-code:text-purple-300
              prose-pre:overflow-x-auto prose-pre:border prose-pre:border-white/10 prose-pre:bg-black/40
              prose-ul:my-4 prose-ul:list-disc prose-ul:pl-5 sm:prose-ul:pl-6
              prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-5 sm:prose-ol:pl-6
              prose-li:mb-2 prose-li:text-gray-300
              prose-blockquote:border-l-4 prose-blockquote:border-purple-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-400
            "
            dangerouslySetInnerHTML={{
              __html: marked.parse(entry.content || entry.markdown || "No content"),
            }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
